/**
 * Brand direction C's start screen (Matt, 2026-10-18; docs/specs/brand-c.md; production since Matt's approval the same
 * day): src/ui/app.ts calls it when __BRAND__ is 'c' (a classic build leaves this module out). The headline, a small payslip being checked (a
 * decorative picture: aria-hidden, its sweep and stamp are CSS in brand/c.css and off under prefers-reduced-motion),
 * one line saying what the app does, the primary action and two secondary ones, then the privacy strip.
 */
type El = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Record<string, string>, ...children: (Node | string)[]) => HTMLElementTagNameMap[K];

/**
 * The picture's payslip, as an Israeli payslip prints it (Hebrew in every language: it's a picture of the document, not
 * UI text). The third row is the one "worth checking".
 */
const PICTURE = { title: 'תלוש שכר', date: '05/2026', rows: [['שכר יסוד', '9,500.00', 'ok'], ['נסיעות', '250.00', 'ok'], ['דמי הבראה', '0.00', 'finding'], ['פנסיה עובד', '570.00', 'ok']] } as const;

export interface HeroSpec {
  el: El;
  tr: (key: string) => string;
  /** The translate button's text key (the first-payslip form with the phonetic gloss, by language). */
  translateKey?: string;
  onPayslip: () => void;
  /** Absent while Form 106 isn't offered (MAGIA_FORM106=0): no button, never a payslip check under its name. */
  onForm106?: () => void;
  onTranslate: () => void;
  /** The checks counted so far, already worded, with the link to the counter's switch (or empty). */
  checks: (Node | string)[];
}

export function brandHero(s: HeroSpec): HTMLElement {
  const { el, tr } = s;
  const btn = (label: string, onClick: () => void, variant: 'primary' | 'secondary', id: string) => {
    const b = el('button', { type: 'button', class: variant, id }, label);
    b.addEventListener('click', onClick);
    return b;
  };
  const picture = el('div', { class: 'hero-payslip', 'aria-hidden': 'true', dir: 'rtl', lang: 'he' },
    el('div', { class: 'hero-payslip-head' }, el('span', {}, PICTURE.title), el('span', { class: 'mono' }, PICTURE.date)),
    ...PICTURE.rows.map(([label, amount, kind], i) => el('div', { class: `hero-row ${kind} r${i}` },
      el('span', { class: 'hero-mark' }, kind === 'ok' ? '✓' : '!'), el('span', {}, label), el('span', { class: 'mono' }, amount))),
    el('span', { class: 'hero-stamp' }, 'נבדק ✓'));
  const lock = el('span', { class: 'lock', 'aria-hidden': 'true' });
  lock.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
  return el('section', { class: 'card flow-step hero' },
    el('h2', { id: 'goal-heading', tabindex: '-1', class: 'hero-title' }, tr('brand.headline')),
    picture,
    el('p', { class: 'hero-explain' }, tr('brand.explain')),
    el('div', { class: 'hero-actions' },
      btn(tr('brand.checkPayslip'), s.onPayslip, 'primary', 'hero-payslip-start'),
      el('div', { class: 'hero-secondary' }, s.onForm106 ? btn(tr('brand.checkForm106'), s.onForm106, 'secondary', 'hero-form106') : '', btn(tr(s.translateKey ?? 'brand.translateInto'), s.onTranslate, 'secondary', 'hero-translate'))),
    el('p', { class: 'privacy-strip' }, lock, tr('brand.privacyStrip')),
    s.checks.length ? el('p', { class: 'help checks-so-far' }, ...s.checks) : '');
}

/** Brand C's logo: a receipt with a zig-zag bottom and a check mark, stroked in the primary colour (follows dark mode). */
export function brandLogo(): HTMLElement {
  const span = document.createElement('span');
  span.className = 'logo logo-c';
  span.setAttribute('aria-hidden', 'true');
  span.innerHTML = '<svg viewBox="0 0 32 32" width="40" height="40" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 3h18v25l-3-2-3 2-3-2-3 2-3-2-3 2z"/><path d="M11.5 15.5l3.2 3.2 6-6.5"/></svg>';
  return span;
}
